import React from 'react';
import {ToastContainer, Slide} from 'react-toastify';

const ToastContext = React.createContext<boolean>(false);

/**
 * Literal CSS length or math expression. Spring tokens like `"$28"` are NOT
 * accepted — values are written verbatim into inline CSS custom properties.
 */
type CssLength =
  | `${number}px`
  | `${number}rem`
  | `calc(${string})`
  | `min(${string})`
  | `max(${string})`
  | `clamp(${string})`;

export type ToastProviderProps = {
  children?: React.ReactNode;
  /**
   * Optional top offset for top-anchored toast containers on non-mobile
   * viewports. Written verbatim into an inline `--spring-toast-top` custom
   * property. Mobile (`bp1Only`) still pins to `top: 0`.
   */
  toastOffsetTop?: CssLength;
  /**
   * Optional right offset for the `top-right` toast container on non-mobile
   * viewports. Written verbatim into an inline `--spring-toast-right` custom
   * property. Useful for aligning toasts with fixed chrome (e.g. a Publish
   * button that sits at the right edge of a centered content max-width).
   * Mobile (`bp1Only`) still expands full-width.
   */
  toastOffsetRight?: CssLength;
};

/**
 * The base ToastProvider. Place at root of app, within the main theme context
 */
const ToastProvider = ({
  children,
  toastOffsetTop,
  toastOffsetRight,
}: ToastProviderProps) => {
  const hasToast = React.useContext(ToastContext);

  if (hasToast) {
    /**
     * We only want to mount ToastContainer once.
     * I would love to do this through a query selector to account for ToastContainers added outside of ToastProvider, but SSR makes that difficult.
     * */
    return children;
  }

  // React's CSSProperties doesn't include CSS custom properties, so cast the
  // literal to CSSProperties.
  const containerStyle =
    toastOffsetTop || toastOffsetRight
      ? ({
          ...(toastOffsetTop && {'--spring-toast-top': toastOffsetTop}),
          ...(toastOffsetRight && {'--spring-toast-right': toastOffsetRight}),
        } as React.CSSProperties)
      : undefined;

  return (
    <ToastContext.Provider value>
      {children}
      <ToastContainer
        autoClose={5000}
        closeButton={false}
        hideProgressBar
        style={containerStyle}
        transition={Slide}
      />
    </ToastContext.Provider>
  );
};

ToastProvider.displayName = 'ToastProvider';

export {ToastProvider};
